import { client } from "~/apollo/client";
import { ComplianceEntitiesDocument } from "~/graphql/supergraphHooks";
import {
  type ComplianceEntitiesQuery,
  type ComplianceEntitiesQueryVariables,
} from "~/graphql/supergraphTypes";
import {
  CSV_EMPTY,
  displayOrDash,
  downloadCSV,
  generateTimestampedCsvFilename,
  toCsv,
  toEnabledDisabled,
} from "~/pages/Compliance/utils/csvExport";
import { fetchAllSlicePages } from "~/pages/Compliance/utils/fetchSlicePages";
import { formatInvestorProgram } from "./InvestorProgramRenderer/formatInvestorProgram";
import type { ComplianceEntityRow } from "./types";

export const ENTITIES_CSV_HEADERS = [
  "Arena ID",
  "Arena name",
  "Entity ID",
  "Entity",
  "Status",
  "Product line",
  "Investor program",
  "Entity program",
  "Onboarding status",
  "Subscriptions",
  "FATCA/CRS",
] as const;

export type EntitiesCsvContext = {
  arenaId: number;
  clientName: string;
};

export const generateEntitiesCsv = (
  rows: ComplianceEntityRow[],
  { arenaId, clientName }: EntitiesCsvContext,
): string =>
  toCsv(
    ENTITIES_CSV_HEADERS,
    rows.map((row) => [
      arenaId,
      clientName || CSV_EMPTY,
      row.id,
      row.name,
      displayOrDash(row.status),
      displayOrDash(row.productLine),
      formatInvestorProgram(row),
      displayOrDash(row.entityProgram),
      row.onboardingStatus,
      toEnabledDisabled(row.complianceEnabled),
      toEnabledDisabled(row.fatcaCrsEnabled),
    ]),
  );

export const fetchAllComplianceEntities = async (
  arenaId: number,
): Promise<ComplianceEntityRow[]> =>
  fetchAllSlicePages(async (offset, limit) => {
    const result = await client.query<
      ComplianceEntitiesQuery,
      ComplianceEntitiesQueryVariables
    >({
      query: ComplianceEntitiesDocument,
      variables: { arenaId, offset, limit },
      fetchPolicy: "no-cache",
    });
    return result.data?.complianceEntitiesSlice ?? [];
  });

export const exportComplianceEntities = async (
  arenaId: number,
  clientName: string,
): Promise<void> => {
  const rows = await fetchAllComplianceEntities(arenaId);
  downloadCSV(
    generateEntitiesCsv(rows, { arenaId, clientName }),
    generateTimestampedCsvFilename("entities"),
  );
};
